import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { CanonicalLoad, NearbyBoxTruckDriver } from '@numeo/types-core'
import { fetchNearbyDriverRoadDistance } from '../services/nearbyDriversApi'
import { DriverRoadDistanceState, IDLE_DRIVER_ROAD_DISTANCE } from '../types/roadDistance'

const MAX_ACTIVE_ROAD_DISTANCE_REQUESTS = 2

interface QueuedRoadDistanceRequest {
    driver: NearbyBoxTruckDriver
    pickup: { lat: number; lng: number }
}

export const useSelectedDriverRoadDistance = (load: CanonicalLoad | null) => {
    const [selectedDriver, setSelectedDriver] = useState<NearbyBoxTruckDriver | null>(null)
    const [statesByDriverId, setStatesByDriverId] = useState<Record<string, DriverRoadDistanceState>>({})
    const statesRef = useRef(statesByDriverId)
    const autoSelectedRef = useRef(false)
    const selectionTouchedRef = useRef(false)
    const activeRequestsRef = useRef(new Map<string, AbortController>())
    const queuedRequestsRef = useRef<QueuedRoadDistanceRequest[]>([])
    const queuedDriverIdsRef = useRef(new Set<string>())
    const processQueueRef = useRef<() => void>(() => undefined)
    statesRef.current = statesByDriverId

    const loadKey = `${load?.externalId ?? load?.id ?? ''}|${load?.pickupLat ?? ''}|${load?.pickupLon ?? ''}`
    useEffect(() => {
        const requests = activeRequestsRef.current
        const queuedDriverIds = queuedDriverIdsRef.current
        requests.forEach((controller) => controller.abort())
        requests.clear()
        queuedRequestsRef.current = []
        queuedDriverIds.clear()
        autoSelectedRef.current = false
        selectionTouchedRef.current = false
        setSelectedDriver(null)
        setStatesByDriverId({})
        return () => {
            requests.forEach((controller) => controller.abort())
            requests.clear()
            queuedRequestsRef.current = []
            queuedDriverIds.clear()
        }
    }, [loadKey])

    const runRequest = useCallback(async ({ driver, pickup }: QueuedRoadDistanceRequest) => {
        const controller = new AbortController()
        activeRequestsRef.current.set(driver.driverId, controller)

        try {
            const result = await fetchNearbyDriverRoadDistance(driver.driverId, pickup, driver.lastSeenAt, controller.signal)
            if (controller.signal.aborted || activeRequestsRef.current.get(driver.driverId) !== controller) return
            if (result.driverId !== driver.driverId || result.driverLocationFixAt !== driver.lastSeenAt) {
                setStatesByDriverId((current) => ({
                    ...current,
                    [driver.driverId]: { status: 'error', message: 'Driver location changed since this route was calculated.' },
                }))
                return
            }
            setStatesByDriverId((current) => ({
                ...current,
                [driver.driverId]: {
                    status: 'success',
                    roadDistanceMi: result.roadDistanceMi,
                    driverLocationFixAt: result.driverLocationFixAt,
                },
            }))
        } catch (error) {
            if (controller.signal.aborted) return
            setStatesByDriverId((current) => ({
                ...current,
                [driver.driverId]: { status: 'error', message: error instanceof Error ? error.message : 'Couldn’t calculate road miles.' },
            }))
        } finally {
            if (activeRequestsRef.current.get(driver.driverId) === controller) activeRequestsRef.current.delete(driver.driverId)
            processQueueRef.current()
        }
    }, [])

    const processQueue = useCallback(() => {
        while (activeRequestsRef.current.size < MAX_ACTIVE_ROAD_DISTANCE_REQUESTS && queuedRequestsRef.current.length > 0) {
            const request = queuedRequestsRef.current.shift()
            if (!request) return
            queuedDriverIdsRef.current.delete(request.driver.driverId)
            void runRequest(request)
        }
    }, [runRequest])
    processQueueRef.current = processQueue

    const calculate = useCallback(
        (driver: NearbyBoxTruckDriver) => {
            const lat = load?.pickupLat
            const lng = load?.pickupLon
            if (typeof lat !== 'number' || !Number.isFinite(lat) || typeof lng !== 'number' || !Number.isFinite(lng) || !driver.lastSeenAt) {
                setStatesByDriverId((current) => ({
                    ...current,
                    [driver.driverId]: { status: 'error', message: 'Road miles require current driver and pickup locations.' },
                }))
                return
            }
            if (activeRequestsRef.current.has(driver.driverId) || queuedDriverIdsRef.current.has(driver.driverId)) return

            queuedRequestsRef.current.push({ driver, pickup: { lat, lng } })
            queuedDriverIdsRef.current.add(driver.driverId)
            setStatesByDriverId((current) => ({ ...current, [driver.driverId]: { status: 'loading' } }))
            processQueue()
        },
        [load?.pickupLat, load?.pickupLon, processQueue]
    )

    const selectDriver = useCallback(
        (driver: NearbyBoxTruckDriver | null) => {
            selectionTouchedRef.current = true
            setSelectedDriver(driver)
            if (!driver) return
            const existing = statesRef.current[driver.driverId]
            if (existing?.status === 'loading' || (existing?.status === 'success' && existing.driverLocationFixAt === driver.lastSeenAt)) return
            void calculate(driver)
        },
        [calculate]
    )

    const selectInitialDriver = useCallback(
        (driver: NearbyBoxTruckDriver) => {
            if (selectionTouchedRef.current || autoSelectedRef.current) return
            autoSelectedRef.current = true
            setSelectedDriver(driver)
            const existing = statesRef.current[driver.driverId]
            if (existing?.status === 'loading' || (existing?.status === 'success' && existing.driverLocationFixAt === driver.lastSeenAt)) return
            void calculate(driver)
        },
        [calculate]
    )

    const driverLocationChanged = useCallback(
        (driver: NearbyBoxTruckDriver) => {
            const activeRequest = activeRequestsRef.current.get(driver.driverId)
            activeRequest?.abort()
            if (activeRequest) activeRequestsRef.current.delete(driver.driverId)
            if (queuedDriverIdsRef.current.delete(driver.driverId)) {
                queuedRequestsRef.current = queuedRequestsRef.current.filter((request) => request.driver.driverId !== driver.driverId)
            }
            setStatesByDriverId((current) => {
                if (!(driver.driverId in current)) return current
                const next = { ...current }
                delete next[driver.driverId]
                return next
            })
            if (selectedDriver?.driverId !== driver.driverId) return
            setSelectedDriver(driver)
            void calculate(driver)
        },
        [calculate, selectedDriver?.driverId]
    )
    const selectedState = useMemo(
        () => (selectedDriver ? (statesByDriverId[selectedDriver.driverId] ?? IDLE_DRIVER_ROAD_DISTANCE) : IDLE_DRIVER_ROAD_DISTANCE),
        [selectedDriver, statesByDriverId]
    )
    const actions = useMemo(() => ({ selectDriver, selectInitialDriver, driverLocationChanged }), [selectDriver, selectInitialDriver, driverLocationChanged])

    return { selectedDriver, selectedState, statesByDriverId, actions }
}
